import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, NIVEAUX_SCOLAIRES } from "../context/AuthContext";
import { 
  listStudents, createStudent, deleteStudent, updateStudent,
  listProfiles, getStudentSessions,
  listParents, createParent, deleteParent,
  generateShareLink, sendParentEmail
} from "../lib/api";
import { 
  Users, Plus, Trash2, BookOpen, Clock, Award, ChevronRight, Zap,
  ChevronLeft, Copy, Check, UserPlus, Mail, Key, Share2, Send, Loader, Link2,
  Settings2
} from "lucide-react";
import { StudentDisplayPreferencesDialog } from "../components/StudentDisplayPreferencesDialog";

const NIVEAU_COLORS = {
  gs: { bg: "bg-pink-100", text: "text-pink-700", badge: "bg-pink-400" },
  cp: { bg: "bg-blue-100", text: "text-blue-700", badge: "bg-blue-400" },
  ce1: { bg: "bg-green-100", text: "text-green-700", badge: "bg-green-400" },
  ce2: { bg: "bg-yellow-100", text: "text-yellow-700", badge: "bg-yellow-400" },
  cm1: { bg: "bg-orange-100", text: "text-orange-700", badge: "bg-orange-400" },
  cm2: { bg: "bg-red-100", text: "text-red-700", badge: "bg-red-400" },
};

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  
  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  return (
    <button
      onClick={handleCopy}
      className={`p-2 rounded-lg transition-colors ${copied ? "bg-green-100 text-green-600" : "bg-slate-100 hover:bg-slate-200 text-slate-500"}`}
    >
      {copied ? <Check size={16} /> : <Copy size={16} />}
    </button>
  );
}

function NiveauBadge({ niveau }) {
  const niv = NIVEAUX_SCOLAIRES.find(n => n.id === niveau);
  const colors = NIVEAU_COLORS[niveau] || NIVEAU_COLORS.cp;
  
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${colors.bg} ${colors.text}`}>
      {niv?.shortName || niveau?.toUpperCase()}
    </span>
  );
}

function ParentShareSection({ student }) {
  const [shareToken, setShareToken] = useState(student?.share_token || null);
  const [loading, setLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [parentEmail, setParentEmail] = useState("");
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  const baseUrl = window.location.origin;
  const shareLink = shareToken ? `${baseUrl}/parent/${shareToken}` : null;

  const handleGenerateLink = async () => {
    setLoading(true);
    try {
      const res = await generateShareLink(student.id);
      setShareToken(res.data.share_token);
    } catch (err) {
      console.error("Failed to generate share link:", err);
      alert("Erreur lors de la generation du lien");
    }
    setLoading(false);
  };

  const handleCopyLink = async () => {
    if (shareLink) {
      await navigator.clipboard.writeText(shareLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  const handleSendEmail = async () => {
    if (!parentEmail || !shareToken) return;
    
    setEmailLoading(true);
    try {
      await sendParentEmail(student.id, parentEmail, shareToken);
      setEmailSent(true);
      setShowEmailForm(false);
      setParentEmail("");
      setTimeout(() => setEmailSent(false), 3000);
    } catch (err) {
      console.error("Failed to send email:", err);
      alert("Erreur lors de l'envoi de l'email");
    }
    setEmailLoading(false);
  };

  return (
    <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <Share2 size={16} className="text-indigo-600" />
        <p className="text-sm font-nunito font-bold text-indigo-700">Partager avec les parents</p>
      </div>

      {!shareToken ? (
        <button
          onClick={handleGenerateLink}
          disabled={loading}
          className="w-full py-3 bg-indigo-500 hover:bg-indigo-600 disabled:bg-indigo-300 text-white font-bold rounded-xl flex items-center justify-center gap-2"
        >
          {loading ? (
            <Loader size={18} className="animate-spin" />
          ) : (
            <>
              <Link2 size={18} />
              Generer un lien de suivi
            </>
          )}
        </button>
      ) : (
        <div className="space-y-3">
          {/* Share link */}
          <div className="flex gap-2">
            <input
              type="text"
              value={shareLink}
              readOnly
              className="flex-1 bg-white border border-indigo-200 rounded-lg px-3 py-2 text-sm text-slate-600 truncate"
            />
            <button
              onClick={handleCopyLink}
              className={`px-4 py-2 rounded-lg font-bold text-sm flex items-center gap-1 transition-colors ${
                linkCopied ? "bg-green-500 text-white" : "bg-white border border-indigo-300 text-indigo-600 hover:bg-indigo-100"
              }`}
            >
              {linkCopied ? <Check size={16} /> : <Copy size={16} />}
              {linkCopied ? "Copie!" : "Copier"}
            </button>
          </div>

          {/* Email form */}
          {!showEmailForm ? (
            <button
              onClick={() => setShowEmailForm(true)}
              className="w-full py-2 bg-white border border-indigo-200 text-indigo-600 font-bold rounded-lg flex items-center justify-center gap-2 hover:bg-indigo-100"
            >
              <Mail size={16} />
              Envoyer par email
            </button>
          ) : (
            <div className="bg-white rounded-lg p-3 border border-indigo-200">
              <label className="text-xs font-bold text-slate-500 mb-1 block">Email du parent</label>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  placeholder="parent@email.com"
                  className="flex-1 border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-indigo-400 outline-none"
                />
                <button
                  onClick={handleSendEmail}
                  disabled={emailLoading || !parentEmail}
                  className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 disabled:bg-slate-300 text-white font-bold rounded-lg flex items-center gap-1"
                >
                  {emailLoading ? <Loader size={16} className="animate-spin" /> : <Send size={16} />}
                </button>
              </div>
            </div>
          )}

          {emailSent && (
            <p className="text-center text-sm text-green-600 font-bold">
              <Check size={14} className="inline mr-1" />
              Email envoye avec succes!
            </p>
          )}
        </div>
      )}
    </div>
  );
}

export default function StudentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Forms
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showAddParent, setShowAddParent] = useState(false);
  const [newStudentName, setNewStudentName] = useState("");
  const [newStudentNiveau, setNewStudentNiveau] = useState("cp");
  const [newStudentProfileId, setNewStudentProfileId] = useState("");
  const [newParentName, setNewParentName] = useState("");
  const [newParentStudentId, setNewParentStudentId] = useState("");
  const [newParentEmail, setNewParentEmail] = useState("");
  
  // Selection
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [studentSessions, setStudentSessions] = useState([]);
  const [activeTab, setActiveTab] = useState("students"); // "students" or "parents"
  const [showPreferencesDialog, setShowPreferencesDialog] = useState(false);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const [sRes, pRes, parentsRes] = await Promise.all([
        listStudents(), 
        listProfiles(),
        listParents()
      ]);
      setStudents(sRes.data);
      setProfiles(pRes.data);
      setParents(parentsRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleCreateStudent = async () => {
    if (!newStudentName.trim()) return;
    try {
      await createStudent({ 
        student_name: newStudentName.trim(), 
        niveau: newStudentNiveau,
        base_profile_id: newStudentProfileId || null 
      });
      setNewStudentName("");
      setNewStudentNiveau("cp");
      setShowAddStudent(false);
      await loadData();
    } catch (err) { console.error(err); }
  };

  const handleCreateParent = async () => {
    if (!newParentName.trim() || !newParentStudentId) return;
    try {
      await createParent({
        parent_name: newParentName.trim(),
        student_id: newParentStudentId,
        email: newParentEmail || null
      });
      setNewParentName("");
      setNewParentStudentId("");
      setNewParentEmail("");
      setShowAddParent(false);
      await loadData();
    } catch (err) { console.error(err); }
  };

  const handleDeleteStudent = async (id) => {
    if (!window.confirm("Supprimer cet eleve et ses donnees ?")) return;
    try {
      await deleteStudent(id);
      setStudents((prev) => prev.filter((s) => s.id !== id));
      if (selectedStudent?.id === id) setSelectedStudent(null);
    } catch (err) { console.error(err); }
  };

  const handleDeleteParent = async (id) => {
    if (!window.confirm("Supprimer ce compte parent ?")) return;
    try {
      await deleteParent(id);
      setParents((prev) => prev.filter((p) => p.id !== id));
    } catch (err) { console.error(err); }
  };

  const selectStudent = async (student) => {
    setSelectedStudent(student);
    try {
      const res = await getStudentSessions(student.id);
      setStudentSessions(res.data);
    } catch {
      setStudentSessions([]);
    }
  };

  if (user?.role !== "teacher") {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12 text-center page-enter">
        <img src="/LEcoleDeNavi5.png" alt="Navi" className="h-32 mx-auto mb-4 mascot-float" />
        <h1 className="font-fredoka text-2xl font-bold text-slate-700">Espace enseignant</h1>
        <p className="font-nunito text-slate-500 mt-2">Cette page est reservee aux enseignants.</p>
      </div>
    );
  }

  return (
    <div data-testid="students-page" className="min-h-screen bg-gradient-to-br from-blue-50 to-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-lg border-b border-blue-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              data-testid="back-home-btn"
              onClick={() => navigate("/home")}
              className="p-2 hover:bg-blue-100 rounded-full transition-colors"
            >
              <ChevronLeft className="text-blue-600" size={24} />
            </button>
            <h1 className="font-fredoka text-2xl font-bold text-slate-800 flex items-center gap-2">
              <Users size={24} className="text-blue-500" />
              Gestion des comptes
            </h1>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-4 pb-2 flex gap-2">
          <button
            data-testid="tab-students"
            onClick={() => setActiveTab("students")}
            className={`px-4 py-2 rounded-full font-fredoka font-bold text-sm transition-colors ${
              activeTab === "students" 
                ? "bg-blue-500 text-white" 
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Eleves ({students.length})
          </button>
          <button
            data-testid="tab-parents"
            onClick={() => setActiveTab("parents")}
            className={`px-4 py-2 rounded-full font-fredoka font-bold text-sm transition-colors ${
              activeTab === "parents" 
                ? "bg-green-500 text-white" 
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            Parents ({parents.length})
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* STUDENTS TAB */}
        {activeTab === "students" && (
          <div className="space-y-6">
            {/* Add student button */}
            <div className="flex justify-end">
              <button
                data-testid="add-student-btn"
                onClick={() => setShowAddStudent(!showAddStudent)}
                className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white font-fredoka font-bold py-2.5 px-5 rounded-full shadow-[0_4px_0_rgb(29,78,216)] active:shadow-none active:translate-y-1 transition-all"
              >
                <Plus size={18} />
                Nouvel eleve
              </button>
            </div>

            {/* Add student form */}
            {showAddStudent && (
              <div className="bg-white rounded-2xl border-2 border-blue-200 p-5 page-enter">
                <h3 className="font-fredoka font-bold text-lg text-slate-800 mb-4">Nouvel eleve</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-nunito font-bold text-slate-500 mb-1">Prenom</label>
                    <input
                      data-testid="student-name-input"
                      type="text"
                      value={newStudentName}
                      onChange={(e) => setNewStudentName(e.target.value)}
                      placeholder="Prenom de l'eleve..."
                      className="w-full h-12 bg-white border-2 border-slate-200 rounded-xl px-4 text-base font-nunito font-bold text-slate-700 focus:border-blue-400 focus:ring-4 focus:ring-blue-100 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-nunito font-bold text-slate-500 mb-1">Niveau</label>
                    <select
                      data-testid="student-niveau-select"
                      value={newStudentNiveau}
                      onChange={(e) => setNewStudentNiveau(e.target.value)}
                      className="w-full h-12 bg-white border-2 border-slate-200 rounded-xl px-4 text-base font-nunito font-bold text-slate-600 focus:border-blue-400 outline-none"
                    >
                      {NIVEAUX_SCOLAIRES.map((n) => (
                        <option key={n.id} value={n.id}>{n.shortName} - {n.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-nunito font-bold text-slate-500 mb-1">Profil de lecture (optionnel)</label>
                    <select
                      data-testid="student-profile-select"
                      value={newStudentProfileId}
                      onChange={(e) => setNewStudentProfileId(e.target.value)}
                      className="w-full h-12 bg-white border-2 border-slate-200 rounded-xl px-4 text-base font-nunito font-bold text-slate-600 focus:border-blue-400 outline-none"
                    >
                      <option value="">Profil par defaut</option>
                      {profiles.map((p) => (<option key={p.id} value={p.id}>{p.name}</option>))}
                    </select>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    data-testid="confirm-add-student"
                    onClick={handleCreateStudent}
                    disabled={!newStudentName.trim()}
                    className="flex-1 py-3 bg-blue-500 hover:bg-blue-600 text-white font-fredoka font-bold rounded-xl shadow-[0_4px_0_rgb(29,78,216)] active:shadow-none active:translate-y-1 transition-all disabled:bg-slate-300 disabled:shadow-none"
                  >
                    Creer l'eleve
                  </button>
                  <button 
                    onClick={() => setShowAddStudent(false)} 
                    className="py-3 px-5 bg-slate-100 hover:bg-slate-200 text-slate-600 font-fredoka font-bold rounded-xl"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Students grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Student list */}
              <div className={`space-y-3 ${selectedStudent ? "hidden lg:block" : ""}`}>
                {loading ? (
                  <div className="space-y-3">
                    {[1,2,3].map((i) => (
                      <div key={i} className="bg-white rounded-2xl border-2 border-slate-100 p-5 animate-pulse">
                        <div className="h-5 bg-slate-100 rounded-lg w-2/3 mb-2" />
                        <div className="h-4 bg-slate-50 rounded-lg w-1/2" />
                      </div>
                    ))}
                  </div>
                ) : students.length === 0 ? (
                  <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center">
                    <Users size={36} className="text-slate-300 mx-auto mb-3" />
                    <p className="font-nunito text-slate-400">Aucun eleve pour le moment.</p>
                    <p className="font-nunito text-sm text-slate-400 mt-1">Ajoutez votre premier eleve !</p>
                  </div>
                ) : (
                  students.map((s) => (
                    <div
                      key={s.id}
                      data-testid={`student-card-${s.id}`}
                      onClick={() => selectStudent(s)}
                      className={`bg-white rounded-2xl p-4 cursor-pointer transition-all border-2 group ${
                        selectedStudent?.id === s.id ? "border-blue-400 shadow-md" : "border-slate-100 hover:border-blue-200"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${NIVEAU_COLORS[s.niveau]?.bg || "bg-blue-100"}`}>
                            <span className={`font-fredoka font-bold text-lg ${NIVEAU_COLORS[s.niveau]?.text || "text-blue-600"}`}>
                              {s.student_name[0]?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-fredoka font-bold text-slate-800">{s.student_name}</h3>
                            <div className="flex items-center gap-2 mt-0.5">
                              <NiveauBadge niveau={s.niveau} />
                              <span className="text-slate-400 text-xs">
                                {(s.progress || []).length} texte{(s.progress || []).length !== 1 ? "s" : ""}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            data-testid={`delete-student-${s.id}`}
                            onClick={(e) => { e.stopPropagation(); handleDeleteStudent(s.id); }}
                            className="p-2 rounded-lg text-slate-300 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Trash2 size={14} />
                          </button>
                          <ChevronRight size={16} className="text-slate-300 lg:hidden" />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Detail panel */}
              <div className={`lg:col-span-2 ${selectedStudent ? "block" : "hidden lg:block"}`}>
                {selectedStudent ? (
                  <div className="space-y-6 page-enter">
                    <button
                      data-testid="student-back-btn"
                      onClick={() => setSelectedStudent(null)}
                      className="lg:hidden font-nunito font-bold text-sm text-slate-400 hover:text-slate-600 mb-2 flex items-center gap-1"
                    >
                      <ChevronLeft size={16} />
                      Retour a la liste
                    </button>

                    {/* Student info card */}
                    <div className="bg-white rounded-2xl border-2 border-slate-100 p-5 shadow-md">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-full flex items-center justify-center ${NIVEAU_COLORS[selectedStudent.niveau]?.bg || "bg-blue-100"}`}>
                            <span className={`font-fredoka font-bold text-2xl ${NIVEAU_COLORS[selectedStudent.niveau]?.text || "text-blue-600"}`}>
                              {selectedStudent.student_name[0]?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h2 className="font-fredoka text-xl font-bold text-slate-800">{selectedStudent.student_name}</h2>
                            <div className="flex items-center gap-2 mt-1">
                              <NiveauBadge niveau={selectedStudent.niveau} />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Access code */}
                      <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-nunito font-bold text-orange-700 mb-1">
                              <Key size={14} className="inline mr-1" />
                              Code d'acces eleve
                            </p>
                            <p className="font-fredoka text-2xl font-bold text-orange-600 tracking-wider">
                              {selectedStudent.access_code}
                            </p>
                          </div>
                          <CopyButton text={selectedStudent.access_code} />
                        </div>
                        <p className="text-xs text-orange-500 mt-2">
                          L'eleve utilise ce code pour se connecter
                        </p>
                      </div>

                      {/* Parent Share Section */}
                      <ParentShareSection student={selectedStudent} />

                      {/* Display Preferences Button */}
                      <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 mb-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-nunito font-bold text-indigo-700 mb-1">
                              <Settings2 size={14} className="inline mr-1" />
                              Preferences d'affichage
                            </p>
                            <p className="text-xs text-indigo-500">
                              Police, taille, aides a la lecture...
                            </p>
                          </div>
                          <button
                            data-testid="open-display-prefs"
                            onClick={() => setShowPreferencesDialog(true)}
                            className="px-4 py-2 bg-indigo-500 hover:bg-indigo-600 text-white font-fredoka font-bold rounded-xl transition-colors"
                          >
                            Configurer
                          </button>
                        </div>
                      </div>

                      {/* Stats */}
                      <div className="grid grid-cols-4 gap-2 md:gap-3">
                        <div className="bg-blue-50 rounded-xl p-3 text-center">
                          <BookOpen size={18} className="text-blue-500 mx-auto mb-1" />
                          <p className="font-fredoka font-bold text-lg text-blue-700">{(selectedStudent.progress || []).length}</p>
                          <p className="font-nunito text-[10px] text-blue-400 font-bold uppercase">Textes</p>
                        </div>
                        <div className="bg-orange-50 rounded-xl p-3 text-center">
                          <Award size={18} className="text-orange-500 mx-auto mb-1" />
                          <p className="font-fredoka font-bold text-lg text-orange-700">
                            {(selectedStudent.progress || []).reduce((sum, p) => sum + (p.words_read || 0), 0)}
                          </p>
                          <p className="font-nunito text-[10px] text-orange-400 font-bold uppercase">Mots</p>
                        </div>
                        <div className="bg-green-50 rounded-xl p-3 text-center">
                          <Clock size={18} className="text-green-500 mx-auto mb-1" />
                          <p className="font-fredoka font-bold text-lg text-green-700">
                            {Math.round((selectedStudent.progress || []).reduce((sum, p) => sum + (p.duration_seconds || 0), 0) / 60)}
                          </p>
                          <p className="font-nunito text-[10px] text-green-400 font-bold uppercase">Min</p>
                        </div>
                        <div className="bg-purple-50 rounded-xl p-3 text-center">
                          <Zap size={18} className="text-purple-500 mx-auto mb-1" />
                          <p className="font-fredoka font-bold text-lg text-purple-700">
                            {studentSessions.length > 0
                              ? Math.round(studentSessions.reduce((sum, s) => sum + (s.words_per_minute || 0), 0) / studentSessions.length)
                              : 0}
                          </p>
                          <p className="font-nunito text-[10px] text-purple-400 font-bold uppercase">Mots/min</p>
                        </div>
                      </div>
                    </div>

                    {/* Linked parents */}
                    <div className="bg-white rounded-2xl border-2 border-slate-100 p-5 shadow-md">
                      <h3 className="font-fredoka font-bold text-lg text-slate-800 mb-3 flex items-center gap-2">
                        <UserPlus size={18} className="text-green-500" />
                        Comptes parents lies
                      </h3>
                      {parents.filter(p => p.student_id === selectedStudent.id).length === 0 ? (
                        <p className="text-slate-400 text-sm font-nunito italic">Aucun compte parent lie.</p>
                      ) : (
                        <div className="space-y-2">
                          {parents.filter(p => p.student_id === selectedStudent.id).map(parent => (
                            <div key={parent.id} className="flex items-center justify-between p-3 bg-green-50 rounded-xl">
                              <div>
                                <p className="font-nunito font-bold text-green-700">{parent.parent_name}</p>
                                <p className="text-xs text-green-500">Code: <span className="font-mono font-bold">{parent.access_code}</span></p>
                              </div>
                              <div className="flex items-center gap-2">
                                <CopyButton text={parent.access_code} />
                                <button
                                  onClick={() => handleDeleteParent(parent.id)}
                                  className="p-2 hover:bg-red-100 rounded-lg text-slate-400 hover:text-red-500"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Reading sessions */}
                    {studentSessions.length > 0 && (
                      <div className="bg-white rounded-2xl border-2 border-slate-100 p-5 shadow-md">
                        <h3 className="font-fredoka font-bold text-lg text-slate-800 mb-3 flex items-center gap-2">
                          <Clock size={18} className="text-green-500" />
                          Sessions de lecture
                        </h3>
                        <div className="space-y-2">
                          {studentSessions.slice(0, 5).map((s) => (
                            <div key={s.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-xl">
                              <div>
                                <p className="font-nunito font-bold text-sm text-slate-700">{s.text_title}</p>
                                <p className="font-nunito text-xs text-slate-400">
                                  {new Date(s.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="font-fredoka font-bold text-sm text-green-600">
                                  {Math.floor(s.duration_seconds / 60)}:{(s.duration_seconds % 60).toString().padStart(2, "0")}
                                </p>
                                <p className="font-nunito text-xs text-slate-400">{s.words_per_minute} mots/min</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="hidden lg:flex bg-white rounded-2xl border-2 border-dashed border-slate-200 p-10 items-center justify-center min-h-[300px]">
                    <div className="text-center">
                      <img src="/LEcoleDeNavi5.png" alt="Navi" className="h-24 mx-auto mb-3 opacity-50" />
                      <p className="font-nunito text-slate-400">Selectionnez un eleve pour voir son suivi</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PARENTS TAB */}
        {activeTab === "parents" && (
          <div className="space-y-6">
            {/* Add parent button */}
            <div className="flex justify-end">
              <button
                data-testid="add-parent-btn"
                onClick={() => setShowAddParent(!showAddParent)}
                disabled={students.length === 0}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-fredoka font-bold py-2.5 px-5 rounded-full shadow-[0_4px_0_rgb(22,163,74)] active:shadow-none active:translate-y-1 transition-all disabled:bg-slate-300 disabled:shadow-none"
              >
                <UserPlus size={18} />
                Nouveau parent
              </button>
            </div>

            {students.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-center">
                <p className="text-yellow-700 font-nunito">
                  Creez d'abord des profils eleves avant d'ajouter des parents.
                </p>
              </div>
            )}

            {/* Add parent form */}
            {showAddParent && students.length > 0 && (
              <div className="bg-white rounded-2xl border-2 border-green-200 p-5 page-enter">
                <h3 className="font-fredoka font-bold text-lg text-slate-800 mb-4">Nouveau compte parent</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-nunito font-bold text-slate-500 mb-1">Nom du parent</label>
                    <input
                      data-testid="parent-name-input"
                      type="text"
                      value={newParentName}
                      onChange={(e) => setNewParentName(e.target.value)}
                      placeholder="Papa de Lucas..."
                      className="w-full h-12 bg-white border-2 border-slate-200 rounded-xl px-4 text-base font-nunito font-bold text-slate-700 focus:border-green-400 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-nunito font-bold text-slate-500 mb-1">Eleve lie</label>
                    <select
                      data-testid="parent-student-select"
                      value={newParentStudentId}
                      onChange={(e) => setNewParentStudentId(e.target.value)}
                      className="w-full h-12 bg-white border-2 border-slate-200 rounded-xl px-4 text-base font-nunito font-bold text-slate-600 focus:border-green-400 outline-none"
                    >
                      <option value="">Choisir un eleve...</option>
                      {students.map((s) => (
                        <option key={s.id} value={s.id}>{s.student_name} ({s.niveau?.toUpperCase()})</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-nunito font-bold text-slate-500 mb-1">Email (optionnel)</label>
                    <input
                      data-testid="parent-email-input"
                      type="email"
                      value={newParentEmail}
                      onChange={(e) => setNewParentEmail(e.target.value)}
                      placeholder="parent@email.com"
                      className="w-full h-12 bg-white border-2 border-slate-200 rounded-xl px-4 text-base font-nunito font-bold text-slate-700 focus:border-green-400 focus:ring-4 focus:ring-green-100 transition-all outline-none"
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button
                    data-testid="confirm-add-parent"
                    onClick={handleCreateParent}
                    disabled={!newParentName.trim() || !newParentStudentId}
                    className="flex-1 py-3 bg-green-500 hover:bg-green-600 text-white font-fredoka font-bold rounded-xl shadow-[0_4px_0_rgb(22,163,74)] active:shadow-none active:translate-y-1 transition-all disabled:bg-slate-300 disabled:shadow-none"
                  >
                    Creer le compte
                  </button>
                  <button 
                    onClick={() => setShowAddParent(false)} 
                    className="py-3 px-5 bg-slate-100 hover:bg-slate-200 text-slate-600 font-fredoka font-bold rounded-xl"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}

            {/* Parents list */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {parents.length === 0 ? (
                <div className="col-span-full bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center">
                  <UserPlus size={36} className="text-slate-300 mx-auto mb-3" />
                  <p className="font-nunito text-slate-400">Aucun compte parent pour le moment.</p>
                </div>
              ) : (
                parents.map((parent) => (
                  <div
                    key={parent.id}
                    className="bg-white rounded-2xl border-2 border-slate-100 p-5 hover:border-green-200 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                          <span className="font-fredoka font-bold text-green-600">
                            {parent.parent_name[0]?.toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <h3 className="font-fredoka font-bold text-slate-800">{parent.parent_name}</h3>
                          <p className="text-xs text-slate-400">Parent de {parent.student_name}</p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleDeleteParent(parent.id)}
                        className="p-2 hover:bg-red-50 rounded-lg text-slate-300 hover:text-red-500"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                    
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-nunito text-green-600 mb-0.5">Code d'acces</p>
                          <p className="font-fredoka font-bold text-lg text-green-700 tracking-wider">{parent.access_code}</p>
                        </div>
                        <CopyButton text={parent.access_code} />
                      </div>
                    </div>
                    
                    {parent.email && (
                      <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
                        <Mail size={12} />
                        {parent.email}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* Display Preferences Dialog */}
      <StudentDisplayPreferencesDialog
        open={showPreferencesDialog}
        onOpenChange={setShowPreferencesDialog}
        student={selectedStudent}
        onSave={(prefs) => {
          // Update local state
          setSelectedStudent(prev => ({ ...prev, display_preferences: prefs }));
          setStudents(prev => prev.map(s => 
            s.id === selectedStudent?.id 
              ? { ...s, display_preferences: prefs } 
              : s
          ));
        }}
      />
    </div>
  );
}
